import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ParkCard from "../components/ParkCard";
import PrivateParkCard from "../components/PrivateParkCard";
import { PRIVATE_PARKS } from "../data/privateParks";
import MapView from "../components/MapView";
import LocationAutocomplete from "../components/LocationAutocomplete";
import CategoryFilterBar from "../components/CategoryFilterBar";
import UseMyLocationButton from "../components/UseMyLocationButton";
import JsonLd from "../components/JsonLd";
import Breadcrumbs from "../components/Breadcrumbs";
import SuggestPlaceBanner from "../components/SuggestPlaceBanner";
import { getCategory, getCategoryByUrlSlug, CATEGORIES } from "../lib/categories";
import { placeUrl } from "../lib/placeUrl";
import CategoryHub from "../components/CategoryHub";
import { track } from "../lib/analytics";

const SITE_URL = "https://pawground.app";

// Haversine distance in meters (small enough for our threshold checks)
const distM = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const PAGE_SIZE = 25;

const Search = ({ categoryUrlSlug = null }) => {
  const [params, setParams] = useSearchParams();
  // The new canonical category landing URLs live at /{urlSlug}
  // (e.g. /dog-parks). When the user lands there, App.js passes the
  // matching urlSlug as a prop. We then use the matching category KEY
  // as the search filter — the same key that `?category=` uses.
  const pathCategory = categoryUrlSlug ? getCategoryByUrlSlug(categoryUrlSlug) : null;

  const q = params.get("q") || "";
  const category = (pathCategory
    ? pathCategory.key
    : (params.get("category") || "all")
  ).toLowerCase();
  const isCategoryHub = !!pathCategory;
  const nearMe = params.get("nearMe") === "1";
  // Location-based search state is held in the URL (?lat=&lng=&near=) so
  // refresh and share-links preserve "near my current location" results.
  // Parsed once per render; `userLocation` below is a derived view of
  // these — never set via useState anymore. The old nearMe=1 flag is
  // still honored as a transient entry point from Landing (and is
  // converted into lat/lng/near as soon as coords resolve).
  const urlLat = parseFloat(params.get("lat"));
  const urlLng = parseFloat(params.get("lng"));
  const urlNear = params.get("near") || "";
  const hasUrlLocation = Number.isFinite(urlLat) && Number.isFinite(urlLng);
  const userLocation = hasUrlLocation ? { lat: urlLat, lng: urlLng } : null;
  const [input, setInput] = useState(q || urlNear);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // True when the most-recent /places call carried the
  // `X-Pawground-Upstream: degraded` header. Drives the
  // "Search is temporarily limited" notice that replaces or
  // augments the "No places found" empty state.
  const [degraded, setDegraded] = useState(false);
  // Pagination is offset-based: nextOffsetRef tracks the offset for the
  // NEXT "Load more" call. Kept in a ref (not state) so it doesn't trigger
  // re-renders and isn't captured stale in handlers.
  const nextOffsetRef = useRef(0);
  // The current "search context" — used as the offset basis for pagination.
  // When q/category/userLocation/areaSearchActive change, we reset
  // pagination and refetch from offset=0.
  const lastQueryRef = useRef(null);
  const [areaSearching, setAreaSearching] = useState(false);
  const [areaSearchActive, setAreaSearchActive] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Private-parks view is purely client-side — no Google Places fetch,
  // no map markers (yet). Just shows the curated PRIVATE_PARKS registry
  // filtered by the active query if there is one.
  const isPrivateView = category === "private";
  const privateResults = useMemo(() => {
    if (!isPrivateView) return [];
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return PRIVATE_PARKS;
    return PRIVATE_PARKS.filter((p) => {
      const hay = `${p.name} ${p.city} ${p.state} ${p.address}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [isPrivateView, q]);

  // Tracks the lat/lng of the LAST successful map-area search so we can
  // skip refiring when the user pans <500m. Set inside handleAreaChange
  // right before the API call (not after the parks land — that anchor
  // would drift toward the centroid of returned markers, not the actual
  // search center, defeating the "have we moved meaningfully?" check).
  const lastSearchCenterRef = useRef(null);
  const areaDebounceRef = useRef(null);
  const [fitToken, setFitToken] = useState(0);
  // `userLocation` is derived from the URL (?lat=&lng=) above — not
  // held in local state anymore. That means refresh / share-link
  // naturally restores the location-based search without any
  // sessionStorage dance.
  // If we navigated from Landing with ?nearMe=1 AND have cached coords, we
  // consume the cache once on mount and skip the default /places fetch so
  // there's no flash of "all places" before the nearby results land.
  const consumedNearMeRef = useRef(false);

  // Client-side personalization re-rank. Reads sessionStorage.pg_personalize
  // (written by the homepage bar) and re-orders the backend's `parks` array
  // by fit score — no new API call, no URL param, no backend change. When
  // no personalization state exists this is a pass-through; existing users
  // see the exact same order as before.
  //
  // Scoring rules (matched against `park.amenities` strings from the doc):
  //   size=small   → +3 "Fenced", +3 "Small dog section"
  //   size=large   → +3 "Off-leash"
  //   size=medium  → +1 "Off-leash", +1 "Fenced"
  //   vibe=zoomies → +2 "Off-leash"
  //   vibe=social  → +2 active_checkins > 0
  //   vibe=chill   → +2 busy_level in {null, "quiet"}
  //   vibe=sniff   → future — no scored signal today
  //
  // Sort is stable (Array.prototype.sort has been stable since ES2019),
  // so equal-score parks preserve backend order. The top result is
  // flagged with a `__greatFit` marker iff its score > 0; the render
  // loop below overlays a "Great fit" badge on that one card.
  const rankedParks = useMemo(() => {
    if (!parks || parks.length === 0) return parks;
    let pref = null;
    try {
      pref = JSON.parse(sessionStorage.getItem("pg_personalize") || "null");
    } catch {
      pref = null;
    }
    if (!pref || !pref.size) return parks;

    const scoreOne = (p) => {
      const am = Array.isArray(p.amenities) ? p.amenities : [];
      let s = 0;
      if (pref.size === "small") {
        if (am.includes("Fenced")) s += 3;
        if (am.includes("Small dog section")) s += 3;
      } else if (pref.size === "large") {
        if (am.includes("Off-leash")) s += 3;
      } else if (pref.size === "medium") {
        if (am.includes("Off-leash")) s += 1;
        if (am.includes("Fenced")) s += 1;
      }
      if (pref.vibe === "zoomies" && am.includes("Off-leash")) s += 2;
      if (pref.vibe === "social" && (p.active_checkins || 0) > 0) s += 2;
      const bl = p.busy_level;
      if (pref.vibe === "chill" && (bl == null || bl === "quiet")) s += 2;
      return s;
    };

    const scored = parks.map((p, i) => ({ p, i, s: scoreOne(p) }));
    scored.sort((a, b) => b.s - a.s || a.i - b.i);
    const out = scored.map((x) => x.p);
    if (scored.length > 0 && scored[0].s > 0) {
      // Non-destructive tag — spread so we don't mutate backend-owned docs.
      out[0] = { ...out[0], __greatFit: true };
    }
    return out;
  }, [parks]);

  // Build a stable query-params object for the current search context.
  // Used both for the initial fetch (offset=0) and "Load more" (offset=N).
  // When ?lat=&lng= is present in the URL, we auto-include them + a
  // default 8km radius so a category chip tapped after "Use my current
  // location" keeps the search anchored on the user, not globally. Any
  // explicit `lat` in `extra` (e.g. map area pan) overrides the URL.
  const buildBaseParams = (extra = {}) => {
    const base = { category, limit: PAGE_SIZE };
    if (q) base.q = q;
    if (hasUrlLocation && !("lat" in extra)) {
      base.lat = urlLat;
      base.lng = urlLng;
      base.radius = 8000;
    }
    return { ...base, ...extra };
  };

  useEffect(() => {
    // Skip the default fetch on the first render when we plan to consume a
    // nearMe cache hit — the nearMe effect below will fire its own fetch.
    if (nearMe && !consumedNearMeRef.current) {
      try {
        const cached = JSON.parse(sessionStorage.getItem("pg_user_location_v1") || "null");
        if (cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) return;
      } catch { /* fall through to default fetch */ }
    }
    // Private view is fully client-side — no Google fetch needed. Clear
    // the regular results so we don't briefly show stale parks while the
    // user navigates between categories.
    if (isPrivateView) {
      setParks([]);
      setHasMore(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setAreaSearchActive(false);
    nextOffsetRef.current = 0;
    lastQueryRef.current = buildBaseParams();
    api
      .get("/places", { params: lastQueryRef.current })
      .then((r) => {
        const items = r.data || [];
        setParks(items);
        setHasMore(items.length >= PAGE_SIZE);
        nextOffsetRef.current = items.length;
        setFitToken((t) => t + 1);
        // Read the upstream-health header (exposed via CORS) so the
        // UI can swap empty-state messaging during a Google outage.
        setDegraded(
          (r.headers?.["x-pawground-upstream"] || "").toLowerCase() === "degraded"
        );
        // Funnel instrumentation — non-location fetch (initial load or
        // query/category change). Location-based fetches fire the same
        // event with has_location: true from `handleUseMyLocation`.
        track("search_performed", {
          query: q || "",
          category,
          result_count: items.length,
          has_location: hasUrlLocation,
        });
      })
      .catch(() => {
        setParks([]);
        setHasMore(false);
        setDegraded(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, nearMe, urlLat, urlLng]);

  // "Load more" — fetches the next page using the LAST query context
  // (whatever lastQueryRef holds: text search, nearby, or area search).
  // Appends to `parks` rather than replacing, so user scroll position
  // is preserved.
  const loadMore = async () => {
    if (loadingMore || !hasMore || !lastQueryRef.current) return;
    setLoadingMore(true);
    try {
      const r = await api.get("/places", {
        params: { ...lastQueryRef.current, offset: nextOffsetRef.current },
      });
      const items = r.data || [];
      // Defensive de-dup: append only items not already present (Google
      // sometimes returns slight overlap across pages).
      setParks((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const fresh = items.filter((p) => !seen.has(p.id));
        return [...prev, ...fresh];
      });
      nextOffsetRef.current += items.length;
      setHasMore(items.length >= PAGE_SIZE);
    } catch {
      toast.error("Couldn't load more results. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  const submit = (picked) => {
    const next = new URLSearchParams(params);
    const v = (picked ?? input ?? "").trim();
    if (v) next.set("q", v);
    else next.delete("q");
    setParams(next);
  };

  const setCategory = (key) => {
    // "Private" isn't a Google Places category — it has its own
    // marketplace surface at /private-parks. Route there unconditionally
    // (from /search, from any category hub, doesn't matter) so the
    // chip is consistent in every state. Previously this fell through
    // to setParams() when on a category hub, leaving the user stranded
    // on /dog-parks?category=private with the path slug overriding the
    // query param and Private silently un-applying.
    if (key === "private") {
      window.history.pushState({}, "", "/private-parks");
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    // If we're on a category hub URL (/dog-parks) and the user picks a
    // different category from the filter bar, navigate to the matching
    // hub URL instead of mutating the query string. Keeps URLs canonical.
    if (isCategoryHub) {
      if (key === "all") {
        window.history.pushState({}, "", "/search");
        // Force react-router to pick up the URL change by reloading the route.
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }
      const target = CATEGORIES.find((c) => c.key === key);
      if (target) {
        window.history.pushState({}, "", `/${target.urlSlug}`);
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }
    }
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("category");
    else next.set("category", key);
    setParams(next);
  };

  // Triggered by the "Use my current location" chip. Writes the
  // coordinates + label to the URL (?lat=&lng=&near=) so refresh /
  // share-link preserves the location-based search. The primary
  // fetch useEffect picks up the lat/lng change via its dependency
  // array and refetches around the new center; the map re-fits via
  // `fitToken` once the data lands.
  const handleUseMyLocation = async ({ lat, lng, label }) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setInput(label || "");
    setAreaSearchActive(false);
    // Anchor the pan-skip reference on the user, not the result
    // centroid — same semantics the old state-driven handler had.
    lastSearchCenterRef.current = { lat, lng };
    setFitToken((t) => t + 1);
    const next = new URLSearchParams(params);
    next.set("lat", String(lat));
    next.set("lng", String(lng));
    if (label) next.set("near", label);
    else next.delete("near");
    // nearMe=1 is a transient entry flag from Landing — now that we
    // have real coords in the URL, drop it so it doesn't replay the
    // geolocation prompt on refresh.
    next.delete("nearMe");
    setParams(next, { replace: true });
  };

  // Consume ?nearMe=1 → deliver a nearby-parks list without asking the
  // user to hunt for the "Use my location" button. Two paths:
  //   (a) sessionStorage already has cached coords (e.g., they tapped
  //       the landing-page location button earlier) → use them silently.
  //   (b) No cache → auto-invoke the browser geolocation prompt with
  //       the same success/error handlers as UseMyLocationButton
  //       (use_my_location_success / use_my_location_error). If the
  //       user denies OR the browser fails, we do NOT surface an
  //       error state — the default no-location place list already
  //       rendered from the primary useEffect stays on screen, and the
  //       "Use my location" button remains available for a manual retry.
  //   `handleUseMyLocation` is now URL-driven: it writes ?lat=&lng=
  //   &near= and deletes nearMe=1 in a single setParams call. The
  //   error path strips nearMe separately so refreshes don't replay
  //   the prompt after a denial.
  useEffect(() => {
    if (!nearMe || consumedNearMeRef.current) return;
    consumedNearMeRef.current = true;

    const stripNearMeParam = () => {
      const next = new URLSearchParams(params);
      next.delete("nearMe");
      setParams(next, { replace: true });
    };

    let cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem("pg_user_location_v1") || "null");
    } catch { /* no cache */ }

    if (cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) {
      // handleUseMyLocation already deletes nearMe from the URL as part
      // of its own setParams — don't double-write or we race ourselves.
      handleUseMyLocation(cached);
      return;
    }

    // No cached coords → cold-start geolocation prompt. Guard against
    // SSR / older browsers that don't expose the API. Silent fallback
    // if unsupported — the default place list from the primary
    // useEffect is already loading.
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      stripNearMeParam();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const payload = { lat, lng, label: "Your location" };
        // Cache so subsequent /search?nearMe=1 visits skip the prompt.
        try {
          sessionStorage.setItem(
            "pg_user_location_v1",
            JSON.stringify(payload),
          );
        } catch { /* private mode — non-fatal */ }
        track("use_my_location_success", { label: payload.label });
        // Writes lat/lng/near and strips nearMe in one setParams call.
        await handleUseMyLocation(payload);
      },
      (err) => {
        // Mirror the button's error branch: track the failure with the
        // same event name/params so both entry points aggregate cleanly
        // in GA4, but do NOT toast — this was an implicit prompt, so a
        // dismissal is a valid user choice, not something we should
        // apologize for. The default place list is already visible.
        track("use_my_location_error", { code: err.code, message: err.message });
        stripNearMeParam();
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearMe]);

  const handleAreaChange = ({ lat, lng, radius }) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    // Cost control: skip the API call when the user pans a trivial
    // distance from the last SEARCH center (not park centroid — we
    // want to know how far they've panned away from where the data
    // was last fetched, regardless of where the markers ended up).
    // 500m threshold keeps street-level inspections free and the
    // 700ms debounce below absorbs typical drag-then-pan-again
    // gestures so we only fire when the user actually settles on
    // a new area to explore.
    const anchor = lastSearchCenterRef.current;
    if (anchor && distM(anchor, { lat, lng }) < 500) return;
    if (areaDebounceRef.current) clearTimeout(areaDebounceRef.current);
    areaDebounceRef.current = setTimeout(async () => {
      // Record the search center BEFORE the API call so subsequent
      // pans compare against where we actually fetched. If we set
      // this in the success branch instead, a slow API call could
      // let several queued pans fire before the anchor moved.
      lastSearchCenterRef.current = { lat, lng };
      setAreaSearching(true);
      nextOffsetRef.current = 0;
      lastQueryRef.current = buildBaseParams({
        lat,
        lng,
        radius: Math.max(2000, Math.min(Math.round(radius || 8000), 50000)),
      });
      try {
        const r = await api.get("/places", { params: lastQueryRef.current });
        const items = r.data || [];
        setParks(items);
        setHasMore(items.length >= PAGE_SIZE);
        nextOffsetRef.current = items.length;
        setAreaSearchActive(true);
      } catch {
        // ignore
      } finally {
        setAreaSearching(false);
      }
    }, 700);
  };

  const headline = useMemo(() => {
    if (isPrivateView) {
      return q ? `Private Parks near "${q}"` : "Private Parks";
    }
    const cat = category === "all" ? null : getCategory(category);
    const placeWord = cat ? cat.label.toLowerCase() : "dog-friendly places";
    if (areaSearchActive) return `${cat ? cat.label : "Places"} in this area`;
    if (q) return `${cat ? cat.label : "Places"} near "${q}"`;
    return `All ${placeWord}`;
  }, [q, category, areaSearchActive, isPrivateView]);

  // NOTE (10/2026): the former legacy-redirect effect that bounced
  // /search?category=X (with no q) over to /{urlSlug} hub pages has
  // been removed. Chips on /search must filter in place so a search
  // that resolved via "Use my current location" (location in URL,
  // no q) or an empty "Find Dog-Friendly Places" submit keeps the
  // user's results on screen. Category-hub URLs are still reachable
  // from Landing's "Jump straight to" row and from the chip bar
  // when the user is already ON a hub (setCategory still routes
  // hub→hub above).

  // Per-route head tags. Server-rendered HTML comes from prerender.js;
  // this updates them after React boots so client navigation between
  // routes stays accurate too. Pattern intentionally mirrors prerender.js.
  useEffect(() => {
    const cat = category === "all" ? null : getCategory(category);
    let title;
    let desc;
    let canonical;
    if (cat) {
      title = `${cat.seoLabel} Near You | Pawground`;
      desc = `Browse ${cat.seoShort} with real ratings, reviews & live activity from local dog parents. Free on Pawground.`;
      canonical = `${SITE_URL}/${cat.urlSlug}`;
    } else if (q) {
      title = `${q} — Dog-Friendly Places | Pawground`;
      desc = `Find dog-friendly places near "${q}" — real check-ins, reviews, and live activity from local dog parents on Pawground.`;
      canonical = `${SITE_URL}/search?q=${encodeURIComponent(q)}`;
    } else {
      title = `Find Dog-Friendly Places Near You | Pawground`;
      desc = `Browse dog parks, pet-friendly restaurants, vets, groomers, and more — with live check-ins from local dog parents.`;
      canonical = `${SITE_URL}/search`;
    }
    document.title = title;

    const setMeta = (sel, attr, value) => {
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement(sel.includes("property=") ? "meta" : sel.includes("name=") ? "meta" : "link");
        if (sel.includes("property=")) el.setAttribute("property", sel.match(/property="([^"]+)"/)[1]);
        else if (sel.includes("name=")) el.setAttribute("name", sel.match(/name="([^"]+)"/)[1]);
        else if (sel.includes("rel=")) el.setAttribute("rel", sel.match(/rel="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);

    // Robots: noindex every /search variant that lacks a user-typed `q`.
    // The hub-page routes (/dog-parks, /vets, …) render through this
    // same component but with `pathCategory` set — their own canonical
    // URLs and inbound links handle indexability, and we don't change
    // their behavior. On /search itself:
    //   • /search                              → noindex (unchanged)
    //   • /search?category=X                   → noindex (new — chips in
    //       place no longer bounce to hub pages, so these URLs now exist
    //       and must not duplicate hub content in Google)
    //   • /search?lat=&lng=&near=              → noindex (new — per-user
    //       geo results, not stable content)
    //   • /search?lat=&lng=&near=&category=X   → noindex (combo of the
    //       two above)
    //   • /search?q=Tampa                      → index (unchanged)
    //   • /search?q=Tampa&category=park        → index (unchanged)
    // We toggle via attribute updates so the tag is cleaned up as the
    // user navigates within the same React session.
    const robotsEl = (() => {
      let el = document.querySelector('meta[name="robots"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "robots");
        document.head.appendChild(el);
      }
      return el;
    })();
    // 11/2026: /search?q=… is noindex too — only sitemap pages index.
    const indexable = isCategoryHub;
    robotsEl.setAttribute("content", indexable ? "index, follow" : "noindex, follow");
  }, [category, q, isCategoryHub]);

  // JSON-LD: ItemList of the visible places so Google understands this
  // page is a curated list of dog-friendly places. Only emit when we
  // have parks to list, otherwise the schema would have an empty itemListElement.
  const searchSchema = useMemo(() => {
    if (!parks.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: headline,
      url: `${SITE_URL}/search${q ? `?q=${encodeURIComponent(q)}` : ""}${category && category !== "all" ? `${q ? "&" : "?"}category=${category}` : ""}`,
      numberOfItems: parks.length,
      itemListElement: parks.slice(0, 20).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}${placeUrl(p)}`,
        name: p.name,
      })),
    };
  }, [parks, headline, q, category]);

  // Category hub mode — when the route is one of the 9 canonical
  // /{urlSlug} URLs AND no query/location/nearMe is set, short-circuit
  // to the static directory grid. Renders inline in the React tree so
  // Googlebot (which sees the React SSR output, not /api/seo/render)
  // gets a real list of city links in the initial HTML. The moment
  // the user picks a city or types a query, we fall through to the
  // normal search UI below.
  if (isCategoryHub && !q && !nearMe && !params.get("near")) {
    return <CategoryHub category={pathCategory} urlSlug={categoryUrlSlug} />;
  }

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] flex flex-col-reverse lg:flex-row">
      <JsonLd schema={searchSchema} id="search-itemlist" />
      {/* Left panel */}
      <div className="w-full lg:w-[48%] xl:w-[44%] lg:overflow-y-auto px-4 sm:px-6 py-6 lg:py-8">
        <Breadcrumbs
          items={
            isCategoryHub
              ? [{ name: pathCategory.seoLabel }]
              : [
                  { name: "Search" },
                  ...(category !== "all"
                    ? [{ name: getCategory(category).seoLabel }]
                    : []),
                ]
          }
        />
        <LocationAutocomplete
          testId="search-input"
          value={input}
          onChange={setInput}
          onPick={(picked) => submit(picked)}
          placeholder="Place name, city, or ZIP"
          size="md"
          showButton={false}
          className="max-w-xl"
        />

        <div className="mt-3">
          <UseMyLocationButton onLocated={handleUseMyLocation} />
        </div>

        {/* Category filter chip bar — primary place-type selector */}
        <div className="mt-5">
          <CategoryFilterBar value={category} onChange={setCategory} />
        </div>

        <div className="flex items-baseline justify-between mt-6">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-ink">{headline}</h1>
          <span className="text-sm text-brand-ink/60 flex items-center gap-1.5">
            {areaSearching && <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-forest" />}
            {isPrivateView
              ? `${privateResults.length} result${privateResults.length === 1 ? "" : "s"}`
              : (loading || areaSearching ? "…" : `${parks.length} result${parks.length === 1 ? "" : "s"}`)}
          </span>
        </div>

        <div className="mt-4 space-y-3" data-testid="parks-list">
          {isPrivateView ? (
            privateResults.length === 0 ? (
              <div className="bg-white rounded-2xl p-6 text-center border border-brand-border-warm">
                <div className="font-heading text-lg font-semibold text-brand-ink">
                  {q ? `No private parks match "${q}"` : "No private parks yet"}
                </div>
                <p className="text-sm text-brand-ink/60 mt-2 leading-relaxed">
                  {q
                    ? "Try clearing the search or browse all categories."
                    : "We're onboarding our first founding hosts. Want to list your yard?"}
                </p>
                <a
                  href="/private-parks/host"
                  data-testid="private-empty-host-cta"
                  className="inline-flex items-center mt-4 px-4 py-2 rounded-full bg-brand-forest hover:bg-[#3d6749] text-white text-sm font-semibold transition"
                >
                  List your yard →
                </a>
              </div>
            ) : (
              privateResults.map((p) => <PrivateParkCard key={p.slug} park={p} />)
            )
          ) : loading ? (
            <div className="text-sm text-brand-ink/60">Loading…</div>
          ) : parks.length === 0 ? (
            <div
              className="bg-white rounded-2xl p-6 text-center border border-brand-border-warm"
              data-testid={degraded ? "search-degraded-empty" : "search-empty"}
            >
              {degraded ? (
                <>
                  <div className="font-heading text-lg font-semibold text-brand-ink">
                    Search is temporarily limited
                  </div>
                  <p className="text-sm text-brand-ink/60 mt-1">
                    We're having trouble reaching our places provider. Try a
                    nearby city or check back in a few minutes — your favorites
                    and check-ins are unaffected.
                  </p>
                </>
              ) : (
                <>
                  <div className="font-heading text-lg font-semibold text-brand-ink">No places found</div>
                  <p className="text-sm text-brand-ink/60 mt-1">Try a different city or category.</p>
                </>
              )}
            </div>
          ) : (
            <>
              {degraded && (
                <div
                  data-testid="search-degraded-banner"
                  className="rounded-2xl px-4 py-3 text-sm border flex items-start gap-2"
                  style={{
                    backgroundColor: "#FFF8EC",
                    borderColor: "#F0D58A",
                    color: "#5A4514",
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <span aria-hidden="true">⚠️</span>
                  <span>
                    Showing cached results — our live places provider is
                    temporarily slow. Fresh data resumes shortly.
                  </span>
                </div>
              )}
              {rankedParks.map((p) =>
                p.__greatFit ? (
                  <div key={p.id} className="relative" data-testid={`great-fit-${p.id}`}>
                    <span
                      className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-brand-forest text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-warm pointer-events-none"
                    >
                      <span aria-hidden>★</span> Great fit
                    </span>
                    <ParkCard
                      park={p}
                      active={activeId === p.id}
                      onHover={setActiveId}
                    />
                  </div>
                ) : (
                  <ParkCard
                    key={p.id}
                    park={p}
                    active={activeId === p.id}
                    onHover={setActiveId}
                  />
                )
              )}
            </>
          )}
        </div>

        {/* Sparse-results CTA: when fewer than 5 results came back AND
            the user actually ran a search (had a query or geo), nudge
            them to add a place. We only show post-loading so it doesn't
            flash during initial render before results land. */}
        {!loading && parks.length > 0 && parks.length < 5 && (
          <SuggestPlaceBanner context="search" />
        )}

        {!loading && parks.length > 0 && hasMore && (
          <div className="mt-6 mb-2 flex justify-center">
            <button
              type="button"
              data-testid="load-more-btn"
              onClick={loadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 bg-white border border-brand-border-warm hover:border-brand-forest hover:bg-brand-light-green rounded-full px-6 py-2.5 text-sm font-semibold text-brand-ink transition-all active:scale-[0.97] disabled:opacity-60 disabled:cursor-wait"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-brand-forest" />
                  Loading more…
                </>
              ) : (
                <>Load more results</>
              )}
            </button>
          </div>
        )}
      </div>

      <div
        data-testid="map-container"
        className="relative w-full h-[380px] lg:flex-1 lg:w-auto lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16 border-b lg:border-b-0 lg:border-l border-brand-border-warm"
      >
        <MapView
          parks={parks}
          activeId={activeId}
          onMarkerClick={setActiveId}
          onAreaChange={handleAreaChange}
          fitToken={fitToken}
          userLocation={userLocation}
        />
        {areaSearching && (
          <div
            data-testid="area-search-indicator"
            className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white border border-brand-border-warm rounded-full shadow-warm-lg px-4 py-1.5 flex items-center gap-2 text-xs font-semibold text-brand-ink"
          >
            <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-forest" />
            Searching this area…
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
